import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Paper, Typography, FormControl, InputLabel, Select, MenuItem,
    TextField, Button, Checkbox, FormControlLabel,
} from '@mui/material';
import { ColDef } from 'ag-grid-community';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { CommodityManagementApi } from '../../../../../../Apis/Apis';
import {
    PositionModel,
    BudgetModel,
    Side,
    OptionStyle,
    GetCustomerDataResponse,
    GetCustomerSettingsResponse,
    LocationModel,
} from '../../../../../../Generated/Commodity-Management-Api';
import { StoneXDatePicker } from '../../../../../StoneXMui';
import { StoneXGrid, StoneXLoading, StoneXRow } from '../../../../../StoneX';
import { useLoadingState } from '../../../../../../Hooks';
import { parseCustomData, getToolDescription } from './Types';

const MMBTU_PER_CONTRACT = 10000;

const MONTHS = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
];

interface CombinedReportRow {
    monthKey: string;
    formattedDate: string;
    tool: string;
    counterpartyName: string;
    settlement: string;               // L / P / E
    tradeDate: string;
    priceTime: string;                // TP
    marketPriceGdi: number;
    decile: string;
    contracts: number;
    coverage: number;                 // fraction
    cumulativeCoverage: number;       // fraction
    maxContracts: number;
    volume: number;                   // MMBtus Purchased Per Month (signed)
    strike: number;
    purchasePrice: number;
    purchaseCost: number;
    expirationDate: string;
    marketPrice: number;
    marketValue: number;
    netVsMarket: number;
    fees: number;
    position: PositionModel;
}

interface CombinedReportProps {
    customerData?: GetCustomerDataResponse;
    customerSettings?: GetCustomerSettingsResponse;
    defaultQuoteDate?: Date;
}

/**
 * L (Last Day Settle), P (Penultimate), or E (European).
 * Matches the legacy Piedmont logic: BP on/after 2010-01-01 => L, else P; append " E" if European.
 */
const getSettlementIndicator = (position: PositionModel): string => {
    const customData = parseCustomData(position.customData);
    let lp = 'P';
    const counterparty = (position.counterparty?.counterpartyName || '').toUpperCase();
    const tradeDate = position.tradeDate ? new Date(position.tradeDate) : null;
    if (counterparty === 'BP' && tradeDate && tradeDate >= new Date('2010-01-01')) {
        lp = 'L';
    }
    const isEuropean =
        Number(customData.optionSettlement) === 2 ||
        position.optionStyle === OptionStyle.European;
    if (isEuropean) {
        lp += ' E';
    }
    return lp;
};

const CombinedReport: React.FC<CombinedReportProps> = ({
    customerData,
    defaultQuoteDate,
}) => {
    const loadingState = useLoadingState();
    const [positions, setPositions] = useState<PositionModel[]>([]);
    const [budgets, setBudgets] = useState<BudgetModel[]>([]);
    const [quoteDate, setQuoteDate] = useState<Date>(defaultQuoteDate || new Date());
    const [hasRun, setHasRun] = useState<boolean>(false);
    const [displayFees, setDisplayFees] = useState<boolean>(false);

    // Default start = first of next month, end = one year from today (match HedgingMTM)
    const [startMonth, setStartMonth] = useState<number>(dayjs().add(1, 'month').month() + 1);
    const [startYear, setStartYear] = useState<number>(dayjs().add(1, 'month').year());
    const [endMonth, setEndMonth] = useState<number>(dayjs().add(1, 'year').month() + 1);
    const [endYear, setEndYear] = useState<number>(dayjs().add(1, 'year').year());

    const locations: LocationModel[] = customerData?.locations || [];
    const [selectedLocation, setSelectedLocation] = useState<number | undefined>(undefined);

    useEffect(() => {
        if (selectedLocation === undefined && locations.length > 0 && locations[0].locationId !== undefined) {
            setSelectedLocation(locations[0].locationId);
        }
    }, [locations, selectedLocation]);

    const selectedLocationName = locations.find(l => l.locationId === selectedLocation)?.locationName || '';

    const fetchData = async () => {
        const posResponse = await CommodityManagementApi.listPositions(
            loadingState.setLoadingState,
            dayjs(quoteDate).format('YYYY-MM-DD'),
            true,  // includeExpirationDates
            true,  // includeMarketPrices
            true,  // useDisplayUnits
            undefined,
            undefined,
            selectedLocation ? [selectedLocation] : undefined,
            undefined,
            undefined,
            undefined,
            startYear,
            startMonth,
            undefined,
            endYear,
            endMonth,
            undefined, // budgetMonthEndFilterLabel
            undefined, // contractMonthStartFilterYear
            undefined, // contractMonthStartFilterMonth
            undefined, // contractMonthStartFilterLabel
            undefined, // contractMonthEndFilterYear
            undefined, // contractMonthEndFilterMonth
            undefined, // contractMonthEndFilterLabel
            undefined, // positionTypesFilter
            undefined, // dealTicketNumberFilter
            undefined, // dealTicketIdFilter
            undefined, // sideFilter
            undefined, // validatedFilter
            undefined, // currencyFilter
            undefined, // currency
            undefined, // expiryStatus
            undefined, // includeAllocations
            undefined, // includeAllocationOffset
            true       // filterByTradeDate - report runs as of the quote date
        );

        const budResponse = await CommodityManagementApi.listBudgets(
            null,
            dayjs(quoteDate).format('YYYY-MM-DD'),
            startYear,
            startMonth,
            undefined,
            endYear,
            endMonth,
            undefined,
            undefined,
            undefined,
            selectedLocation ? [selectedLocation] : undefined,
            undefined,
            undefined,
            undefined,
            false,
            false,
            false
        );

        setPositions(posResponse.data.rows || []);
        setBudgets(budResponse.data.rows || []);
        setHasRun(true);
    };

    const handleSubmit = () => {
        fetchData();
    };

    const commodityName = useMemo(() => {
        return positions.find(p => !p.isExpired)?.commodity?.commodityName || '';
    }, [positions]);

    const reportRows: CombinedReportRow[] = useMemo(() => {
        const open = positions.filter(p => !p.isExpired);
        if (open.length === 0) return [];

        // Budget volume per month (MMBtus) for coverage and Max # Contracts
        const budgetVolumeByMonth = new Map<string, number>();
        budgets.forEach(b => {
            if (b.budgetDate?.year !== undefined && b.budgetDate?.month !== undefined) {
                const key = `${b.budgetDate.year}-${String(b.budgetDate.month).padStart(2, '0')}`;
                budgetVolumeByMonth.set(key, (budgetVolumeByMonth.get(key) ?? 0) + (b.volume ?? 0));
            }
        });

        const rows: CombinedReportRow[] = open.map(position => {
            const customData = parseCustomData(position.customData);
            const y = position.budgetDate?.year ?? 0;
            const m = position.budgetDate?.month ?? 1;
            const monthKey = `${y}-${String(m).padStart(2, '0')}`;
            const volume = position.volume ?? 0;
            const sideMultiplier = position.side === Side.Buy ? 1 : -1;
            const signedVolume = volume * sideMultiplier;
            const price = position.price ?? 0;
            const marketPrice = position.marketPrice ?? 0;
            const purchaseCost = price * signedVolume;
            const marketValue = marketPrice * signedVolume;
            const netVsMarket = marketValue - purchaseCost;
            const budgetVolume = budgetVolumeByMonth.get(monthKey) ?? 0;
            const coverage = budgetVolume > 0 ? signedVolume / budgetVolume : 0;
            const maxContracts = budgetVolume / MMBTU_PER_CONTRACT;
            const contracts = volume / MMBTU_PER_CONTRACT;
            const decileVal = customData.decile;
            const decile = decileVal ? `${decileVal}${Number(decileVal) > 0 ? 'th' : ''}` : '';
            const triggerVal = customData.trigger;
            const priceTime = typeof triggerVal === 'boolean'
                ? String(triggerVal)
                : (triggerVal != null ? String(triggerVal) : '');
            const marketPriceGdi = Number(customData.marketPriceGDI ?? customData.marketpriceGDI ?? 0) || 0;

            return {
                monthKey,
                formattedDate: dayjs(`${y}-${m}-01`).format('MMM-YYYY'),
                tool: getToolDescription(position),
                counterpartyName: position.counterparty?.counterpartyName || '',
                settlement: getSettlementIndicator(position),
                tradeDate: position.tradeDate ? dayjs(position.tradeDate).format('MM/DD/YYYY') : '',
                priceTime,
                marketPriceGdi,
                decile,
                contracts,
                coverage,
                cumulativeCoverage: 0, // filled after sort
                maxContracts,
                volume: signedVolume,
                strike: position.optionStrike ?? 0,
                purchasePrice: price,
                purchaseCost,
                expirationDate: position.expirationDate ? dayjs(position.expirationDate).format('MM/DD/YYYY') : '',
                marketPrice,
                marketValue,
                netVsMarket,
                // Fees in customData are per-contract; multiply by number of contracts for the total.
                // Accept either "fees" (plural) or "fee" (singular) key, matching MtmReport / MtmClosedReport.
                fees: (Number(customData.fees ?? customData.fee ?? 0) || 0) * contracts,
                position,
            };
        });

        rows.sort((a, b) => {
            const dateCompare = a.monthKey.localeCompare(b.monthKey);
            if (dateCompare !== 0) return dateCompare;
            return a.tradeDate.localeCompare(b.tradeDate);
        });

        let monthCumulative = 0;
        let lastMonthKey = '';
        rows.forEach(r => {
            if (r.monthKey !== lastMonthKey) {
                monthCumulative = 0;
                lastMonthKey = r.monthKey;
            }
            monthCumulative += r.coverage;
            r.cumulativeCoverage = monthCumulative;
        });

        return rows;
    }, [positions, budgets]);

    const summary = useMemo(() => {
        return reportRows.reduce((acc, r) => ({
            totalVolume: acc.totalVolume + r.volume,
            totalPurchaseCost: acc.totalPurchaseCost + r.purchaseCost,
            totalMarketValue: acc.totalMarketValue + r.marketValue,
            totalNetVsMarket: acc.totalNetVsMarket + r.netVsMarket,
            totalFees: acc.totalFees + r.fees,
        }), {
            totalVolume: 0,
            totalPurchaseCost: 0,
            totalMarketValue: 0,
            totalNetVsMarket: 0,
            totalFees: 0,
        });
    }, [reportRows]);

    const fmtInt = (v: number | null | undefined) =>
        v != null ? v.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '';
    const fmtPercent = (v: number | null | undefined) =>
        v != null ? (v * 100).toLocaleString(undefined, { maximumFractionDigits: 0 }) + '%' : '';
    const fmtCurrency2 = (v: number | null | undefined) =>
        v != null ? v.toLocaleString(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';
    const fmtCurrency3 = (v: number | null | undefined) =>
        v != null ? '$' + v.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 }) : '';
    const fmtCurrency4 = (v: number | null | undefined) =>
        v != null ? '$' + v.toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 }) : '';

    const columnDefs: ColDef<CombinedReportRow>[] = useMemo(() => {
        const cols: ColDef<CombinedReportRow>[] = [
            { field: 'formattedDate', headerName: 'Period', flex: 1, minWidth: 100, pinned: 'left' },
            { field: 'tool', headerName: 'Tool', flex: 1, minWidth: 110 },
            { field: 'counterpartyName', headerName: 'Counterparty', flex: 1.3, minWidth: 140 },
            { field: 'settlement', headerName: 'Last Day Settle (L) / Penultimate (P) / European (E)', flex: 1.5, minWidth: 150 },
            { field: 'tradeDate', headerName: 'Trade Date', flex: 1, minWidth: 110 },
            { field: 'priceTime', headerName: 'Price / Time', flex: 1, minWidth: 100 },
            { field: 'marketPriceGdi', headerName: 'Price (GDI)', flex: 1, minWidth: 110, type: 'numericColumn', valueFormatter: p => p.value ? fmtCurrency3(p.value) : '' },
            { field: 'decile', headerName: 'Decile', flex: 0.8, minWidth: 80 },
            { field: 'contracts', headerName: 'Contracts', flex: 1, minWidth: 100, type: 'numericColumn', valueFormatter: p => fmtInt(p.value) },
            { field: 'coverage', headerName: '% Coverage', flex: 1, minWidth: 100, type: 'numericColumn', valueFormatter: p => fmtPercent(p.value) },
            { field: 'cumulativeCoverage', headerName: 'Cumulative Coverage', flex: 1.2, minWidth: 130, type: 'numericColumn', valueFormatter: p => fmtPercent(p.value) },
            { field: 'maxContracts', headerName: 'Max # Contracts', flex: 1.2, minWidth: 130, type: 'numericColumn', valueFormatter: p => fmtInt(p.value) },
            { field: 'volume', headerName: 'MMBtus Purchased Per Month', flex: 1.6, minWidth: 180, type: 'numericColumn', valueFormatter: p => fmtInt(p.value) },
            { field: 'strike', headerName: 'Strike/Fixed Price', flex: 1.2, minWidth: 130, type: 'numericColumn', valueFormatter: p => p.value ? '$' + p.value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '' },
            { field: 'purchasePrice', headerName: 'Purchase Price', flex: 1.1, minWidth: 130, type: 'numericColumn', valueFormatter: p => p.value ? fmtCurrency4(p.value) : '' },
            { field: 'purchaseCost', headerName: 'Purchase Cost/Proceeds', flex: 1.3, minWidth: 150, type: 'numericColumn', valueFormatter: p => fmtCurrency2(p.value) },
            { field: 'expirationDate', headerName: 'Trade / Expiration Date', flex: 1.2, minWidth: 140, valueFormatter: p => p.value ? `${p.value} E` : '' },
            { field: 'marketPrice', headerName: 'NYMEX Market Price', flex: 1.2, minWidth: 140, type: 'numericColumn', valueFormatter: p => p.value ? fmtCurrency4(p.value) : '' },
            { field: 'marketValue', headerName: 'Market Value', flex: 1.1, minWidth: 130, type: 'numericColumn', valueFormatter: p => fmtCurrency2(p.value) },
            {
                field: 'netVsMarket', headerName: 'Net Value vs. Current Market Value', flex: 1.5, minWidth: 170, type: 'numericColumn',
                cellStyle: p => ({ color: (p.value ?? 0) >= 0 ? 'green' : 'red' }),
                valueFormatter: p => fmtCurrency2(p.value),
            },
        ];
        if (displayFees) {
            cols.push({
                field: 'fees', headerName: 'Fees', flex: 1, minWidth: 100, type: 'numericColumn',
                valueFormatter: p => fmtCurrency2(p.value),
            });
        }
        return cols;
    }, [displayFees]);

    const handleDownloadExcel = () => {
        if (reportRows.length === 0) return;

        const baseHeader = [
            'Period', 'Tool', 'Counterparty',
            'Last Day Settle (L) / Penultimate (P) / European (E)',
            'Trade Date', 'Price / Time', 'Price (GDI)', 'Decile',
            'Contracts', '% Coverage', 'Cumulative Coverage', 'Max # Contracts',
            'MMBtus Purchased Per Month', 'Strike/Fixed Price', 'Purchase Price',
            'Purchase Cost/Proceeds', 'Trade / Expiration Date',
            'NYMEX Market Price', 'Market Value', 'Net Value vs. Current Market Value',
        ];
        const header = displayFees ? [...baseHeader, 'Fees'] : baseHeader;
        const lastCol = header.length - 1;

        const title = commodityName || 'Combined Position Report';
        const subtitle = `Report as of ${dayjs(quoteDate).format('M/D/YYYY')} Close`;

        const aoa: (string | number)[][] = [];
        aoa.push([title]);
        aoa.push([subtitle]);
        aoa.push(header);

        reportRows.forEach(r => {
            const row: (string | number)[] = [
                r.formattedDate,
                r.tool,
                r.counterpartyName,
                r.settlement,
                r.tradeDate,
                r.priceTime,
                r.marketPriceGdi,
                r.decile,
                r.contracts,
                r.coverage,
                r.cumulativeCoverage,
                r.maxContracts,
                r.volume,
                r.strike,
                r.purchasePrice,
                r.purchaseCost,
                r.expirationDate ? `${r.expirationDate} E` : '',
                r.marketPrice,
                r.marketValue,
                r.netVsMarket,
            ];
            if (displayFees) row.push(r.fees);
            aoa.push(row);
        });

        // SUMMARY row (matches piedmont tfoot layout)
        const summaryRow: (string | number)[] = new Array(header.length).fill('');
        summaryRow[0] = 'SUMMARY:';
        summaryRow[12] = summary.totalVolume;           // MMBtus Purchased Per Month
        summaryRow[15] = summary.totalPurchaseCost;     // Purchase Cost/Proceeds
        summaryRow[18] = summary.totalMarketValue;      // Market Value
        summaryRow[19] = summary.totalNetVsMarket;      // Net Value vs. Current Market Value
        if (displayFees) summaryRow[20] = summary.totalFees;
        aoa.push(summaryRow);

        const sheet = XLSX.utils.aoa_to_sheet(aoa);
        sheet['!merges'] = [
            { s: { r: 0, c: 0 }, e: { r: 0, c: lastCol } },
            { s: { r: 1, c: 0 }, e: { r: 1, c: lastCol } },
        ];

        const currency2 = '"$"#,##0.00;[Red]("$"#,##0.00)';
        const currency3 = '"$"#,##0.000;[Red]("$"#,##0.000)';
        const currency4 = '"$"#,##0.0000;[Red]("$"#,##0.0000)';
        const intFmt = '#,##0;(#,##0)';
        const pctFmt = '0%';

        const dataStart = 3;
        for (let i = 0; i < reportRows.length + 1; i++) {
            const r = dataStart + i;
            const apply = (c: number, fmt: string) => {
                const ref = XLSX.utils.encode_cell({ r, c });
                const cell = sheet[ref];
                if (cell && typeof cell.v === 'number') {
                    cell.t = 'n';
                    cell.z = fmt;
                }
            };
            apply(6, currency3);   // Price (GDI)
            apply(8, intFmt);      // Contracts
            apply(9, pctFmt);      // % Coverage
            apply(10, pctFmt);     // Cumulative Coverage
            apply(11, intFmt);     // Max # Contracts
            apply(12, intFmt);     // MMBtus
            apply(13, currency2);  // Strike
            apply(14, currency4);  // Purchase Price
            apply(15, currency2);  // Purchase Cost
            apply(17, currency4);  // NYMEX Market Price
            apply(18, currency2);  // Market Value
            apply(19, currency2);  // Net Value
            if (displayFees) apply(20, currency2);
        }

        sheet['!cols'] = header.map(h =>
            ({ wch: Math.min(Math.max(h.length, 10), 30) }));

        const workbook = XLSX.utils.book_new();
        const sheetName = (selectedLocationName || 'Combined').replace(/[\\/*?:[\]]/g, '').substring(0, 31) || 'Combined';
        XLSX.utils.book_append_sheet(workbook, sheet, sheetName);

        const filename = `CombinedPositionReport_${dayjs(quoteDate).format('YYYYMMDD')}.xlsx`;
        XLSX.writeFile(workbook, filename);
    };

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h5" gutterBottom>Combined Position Report</Typography>

            <Paper sx={{ p: 2, mb: 2 }}>
                <StoneXRow>
                    <StoneXDatePicker
                        label="Marking to Market Date"
                        value={quoteDate}
                        onChange={(date) => setQuoteDate(date || new Date())}
                    />
                </StoneXRow>
                <StoneXRow>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Start Month</InputLabel>
                        <Select
                            value={startMonth}
                            label="Start Month"
                            onChange={(e) => setStartMonth(e.target.value as number)}
                        >
                            {MONTHS.map(m => (
                                <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        label="Start Year"
                        type="number"
                        size="small"
                        value={startYear}
                        onChange={(e) => setStartYear(parseInt(e.target.value) || dayjs().year())}
                        sx={{ width: 100 }}
                    />
                </StoneXRow>
                <StoneXRow>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>End Month</InputLabel>
                        <Select
                            value={endMonth}
                            label="End Month"
                            onChange={(e) => setEndMonth(e.target.value as number)}
                        >
                            {MONTHS.map(m => (
                                <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        label="End Year"
                        type="number"
                        size="small"
                        value={endYear}
                        onChange={(e) => setEndYear(parseInt(e.target.value) || dayjs().year())}
                        sx={{ width: 100 }}
                    />
                </StoneXRow>
                <StoneXRow>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Location</InputLabel>
                        <Select
                            value={selectedLocation ?? ''}
                            label="Location"
                            onChange={(e) => setSelectedLocation(Number(e.target.value))}
                        >
                            {locations.map((l: LocationModel) => (
                                <MenuItem key={l.locationId} value={l.locationId}>{l.locationName}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </StoneXRow>
                <StoneXRow>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={displayFees}
                                onChange={(e) => setDisplayFees(e.target.checked)}
                            />
                        }
                        label="Display Fees"
                    />
                </StoneXRow>
                <StoneXRow>
                    <Button variant="contained" onClick={handleSubmit}>Submit</Button>
                    <Button
                        variant="outlined"
                        onClick={handleDownloadExcel}
                        disabled={!hasRun || reportRows.length === 0}
                        sx={{ ml: 1 }}
                    >
                        Save to Excel
                    </Button>
                </StoneXRow>
            </Paper>

            {loadingState.isLoading() ? (
                <StoneXLoading show />
            ) : hasRun ? (
                <>
                    {commodityName && (
                        <Typography variant="h6" sx={{ mt: 1 }}>{commodityName}</Typography>
                    )}
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        {`Report as of ${dayjs(quoteDate).format('M/D/YYYY')} Close`}
                    </Typography>
                    <StoneXGrid
                        columns={columnDefs}
                        rows={reportRows}
                        gridOptions={{
                            headerHeight: 72,
                            defaultColDef: {
                                wrapHeaderText: true,
                                autoHeaderHeight: true,
                            },
                        }}
                    />

                    <Paper sx={{ p: 2, mt: 2 }}>
                        <Typography variant="h6">Summary</Typography>
                        <Box sx={{ display: 'flex', gap: 4, mt: 1, flexWrap: 'wrap' }}>
                            <Typography>
                                <strong>Total MMBtus:</strong> {fmtInt(summary.totalVolume)}
                            </Typography>
                            <Typography>
                                <strong>Total Purchase Cost/Proceeds:</strong> {fmtCurrency2(summary.totalPurchaseCost)}
                            </Typography>
                            <Typography>
                                <strong>Total Market Value:</strong> {fmtCurrency2(summary.totalMarketValue)}
                            </Typography>
                            <Typography>
                                <strong>Total Net Value vs. Current Market Value:</strong>
                                <span style={{ color: summary.totalNetVsMarket >= 0 ? 'green' : 'red' }}>
                                    {' '}{fmtCurrency2(summary.totalNetVsMarket)}
                                </span>
                            </Typography>
                            {displayFees && (
                                <Typography>
                                    <strong>Total Fees:</strong> {fmtCurrency2(summary.totalFees)}
                                </Typography>
                            )}
                        </Box>
                    </Paper>
                </>
            ) : null}
        </Box>
    );
};

export default CombinedReport;
